import { toErrorText } from '../../../utils/errorMessage';
/**
 * accessRevocation - revoking and restoring a person's access to the app.
 *
 * The two endpoints and the two pieces of reading that go with them: what a
 * refusal MEANS, and what a success actually did. Both are pure so they can be
 * pinned by tests, because both are the part that is easy to get quietly wrong
 * - a mapped error that falls through to "Something went wrong" tells an admin
 * nothing about the one thing they need to do next.
 *
 * ADDRESSED BY PERSON UUID. `/admin/users` does not carry one - see
 * `peopleDirectory`, which is why the admin pages already fetch
 * `/org-people/people`. A caller with no uuid must not build a path anyway:
 * `/people/undefined/revoke-access` is a 404 that looks like a backend fault.
 */

export const revokeAccessPath = (personUuid: string): string =>
  `/org-people/people/${personUuid}/revoke-access`;

export const restoreAccessPath = (personUuid: string): string =>
  `/org-people/people/${personUuid}/restore-access`;

/** The 409 codes both endpoints answer with. */
export type RevocationCode =
  | 'NO_ALTERNATE_ADMIN'
  | 'ALREADY_REVOKED'
  | 'CANNOT_REVOKE_SELF'
  | 'NOT_REVOKED';

/**
 * The message an admin should read, keyed on the CODE and not on the prose.
 *
 * The backend sends a full English `error` alongside the code. Matching on that
 * string instead would break the first time it is reworded or localised, so the
 * code decides and the server's own sentence is the fallback for anything this
 * does not know about.
 *
 * `NO_ALTERNATE_ADMIN` is the one that matters: it is not a failure, it is a
 * prerequisite, and the message has to say what to do rather than what went
 * wrong.
 */
export const revocationErrorMessage = (
  error: any,
  t: (key: string) => string,
): string => {
  const status = error?.response?.status;
  const body = error?.response?.data || {};
  const code = typeof body.code === 'string' ? body.code : '';

  switch (code) {
    case 'NO_ALTERNATE_ADMIN':
      return t('Assign another admin before revoking this user');
    case 'ALREADY_REVOKED':
      return t('This person’s access has already been revoked.');
    case 'CANNOT_REVOKE_SELF':
      return t('You cannot revoke your own access.');
    case 'NOT_REVOKED':
      return t('This person’s access is already active.');
    default:
      break;
  }

  if (status === 403) return t('You do not have permission to change this person’s access.');
  if (status === 404) return t('That person could not be found.');

  return toErrorText(body.error) || toErrorText(body.message) || t('Something went wrong. Please try again.');
};

/**
 * "3 matters and 12 tasks reassigned to you", when the response says so.
 *
 * `metrics` is a bag of counts whose exact keys are the backend's business, so
 * this reads what it recognises and counts anything else generically rather
 * than asserting a shape it was not promised. Zeroes are DROPPED: "0 tasks
 * reassigned" is noise, and a metrics bag of all zeroes produces no sentence at
 * all rather than a boast about nothing.
 */
const METRIC_LABELS: Record<string, { one: string; many: string }> = {
  matters: { one: 'matter', many: 'matters' },
  matters_reassigned: { one: 'matter', many: 'matters' },
  tasks: { one: 'task', many: 'tasks' },
  tasks_reassigned: { one: 'task', many: 'tasks' },
  cases: { one: 'case', many: 'cases' },
  cases_reassigned: { one: 'case', many: 'cases' },
  projects: { one: 'project', many: 'projects' },
  projects_reassigned: { one: 'project', many: 'projects' },
};

export const reassignmentSummary = (
  metrics: any,
  t: (key: string) => string,
): string | null => {
  if (!metrics || typeof metrics !== 'object') return null;

  const parts: string[] = [];
  for (const [key, raw] of Object.entries(metrics)) {
    const count = typeof raw === 'number' ? raw : Number.NaN;
    if (!Number.isFinite(count) || count <= 0) continue;
    const label = METRIC_LABELS[key];
    const noun = label
      ? t(count === 1 ? label.one : label.many)
      // An unrecognised counter still says something true: its own name,
      // readable, rather than being dropped or guessed at.
      : key.replace(/_reassigned$/, '').replace(/_/g, ' ');
    parts.push(`${count} ${noun}`);
  }
  if (!parts.length) return null;

  const list = parts.length === 1
    ? parts[0]
    : `${parts.slice(0, -1).join(', ')} ${t('and')} ${parts[parts.length - 1]}`;
  return `${list} ${t('reassigned to you')}`;
};

/** Read the person's post-action active state out of a 200 body. */
export const readActiveState = (payload: any): boolean | null => {
  const data = payload?.data ?? payload;
  if (typeof data?.is_active === 'boolean') return data.is_active;
  // `revoked` is the same fact stated the other way round.
  if (typeof data?.revoked === 'boolean') return !data.revoked;
  return null;
};
